import { EditOutlined, SelectOutlined, DeleteOutlined, PlusOutlined, ExclamationCircleOutlined, FormOutlined, ExportOutlined } from "@ant-design/icons";
import { App, Button, Table, Space, Input, Modal, Form, Row, Select, Card, Grid, Tabs, Checkbox, message, Dropdown, Tag, Tooltip, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { ExclusionCriteria, InclusionCriteria, Patient, PatientAdmission, isPatientEligible, parseScreeningFailures } from "../models/models";
import { deletePatient, postPatient, postPatientAdmission } from "../models/serverApi";
import { PatientAdmissionPopup } from "./PatientAdmission";
import { useNavigate } from "react-router-dom";

export const PatientsTable: React.FC<{ patients: Patient[], patientAdmissions: PatientAdmission[], afterEdit?: (patient?: Patient) => void, afterDelete?: (patient: Patient) => void, selectionButton?: boolean, selectPatient?: (patient?: Patient) => void }> =
    ({ patients, patientAdmissions, afterEdit: onEdit, afterDelete: onDelete, selectionButton, selectPatient }) => {
        const [formVisible, setFormVisible] = useState(false);
        const [admissionFormVisible, setAdmissionFormVisible] = useState(false);
        const [currentPatient, setCurrentPatient] = useState<Patient | undefined>(undefined);
        const [patientAdmissionsFilter, setPatientAdmissionsFilter] = useState("");
        const [currentPatientAdmission, setCurrentPatientAdmission] = useState<PatientAdmission | undefined>(undefined);
        const navigate = useNavigate();
        const screens = Grid.useBreakpoint();
        const [context, setContext] = useContext(AppContext);

        const showForm = (patient: Patient | undefined) => {
            setCurrentPatient(patient);
            setFormVisible(true);
        };

        const showFormAdmission = (patient: Patient | undefined) => {
            setCurrentPatientAdmission({ notes: "", diagnosis: "", patientId: patient?.id } as any);
            setAdmissionFormVisible(true);
        };

        const columns = [
            {
                title: 'Γεν. Κωδικός',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: 'Κωδικος ασθενούς',
                dataIndex: 'patientName',
                key: 'patientName',
            },
            {
                title: 'Περιγραφή',
                dataIndex: 'patientDescription',
                key: 'patientDescription',

            },
            // "Ομάδα ασθενούς" (Patient.patientClass) is hidden for this deployment -
            // the protocol has no treatment groups, so the column was always empty or
            // misleading. The field itself is untouched: it is still stored, still
            // editable, and still exported, so re-enabling this is a one-line change.
            // {
            //     title: 'Ομάδα ασθενούς',
            //     render: (values: Patient, record: Patient, index: number) => {
            //         return <>{record.patientClass}</>;
            //     }
            // },
            {
                title: 'Κέντρο',
                render: (values: Patient, record: Patient, index: number) => {
                    return <>{record.clinicalTrial?.title}</>;
                }
            },
            {
                title: 'Επιλέξιμος',
                key: 'eligibility',
                // Sorted and filtered on, because "show me everyone we screened out"
                // is the question this column exists to answer.
                filters: [
                    { text: 'Επιλέξιμοι', value: 'yes' },
                    { text: 'Μη επιλέξιμοι', value: 'no' },
                ],
                onFilter: (value: any, record: Patient) =>
                    (isPatientEligible(record) ? 'yes' : 'no') === value,
                render: (values: Patient, record: Patient) => {
                    if (isPatientEligible(record)) {
                        return <Tag color="green">Ναι</Tag>;
                    }

                    // The reasons are on the row itself, in a tooltip rather than a
                    // column: they are long free text and would wreck the layout,
                    // but they are the whole point of recording the patient.
                    const f = parseScreeningFailures(record.screeningFailuresJson);
                    const reasons = [
                        ...f.failedInclusion.map(t => `Δεν πληροί: ${t}`),
                        ...f.metExclusion.map(t => `Αποκλεισμός: ${t}`),
                    ];

                    return (
                        <Tooltip
                            title={reasons.length > 0
                                ? <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                                    {reasons.map(r => <li key={r}>{r}</li>)}
                                </ul>
                                : "Δεν καταγράφηκε ο λόγος"}
                        >
                            <Tag color="red" style={{ cursor: "help" }}>Όχι</Tag>
                        </Tooltip>
                    );
                }
            },
            {
                title: '',
                key: 'action',
                render: (text: any, record: Patient) => (
                    <Space.Compact>
                        {selectionButton && <Button type="primary" icon={<SelectOutlined />} onClick={() => selectPatient?.(record)}>
                            Επιλογή
                        </Button>}
                        <Button type="primary" icon={<EditOutlined />} onClick={() => showForm(record)}>
                            Επεξεργασία
                        </Button>
                        {/* <Button type="primary" icon={<PlusOutlined />} onClick={() => showFormAdmission(record)}>
                            Προσθήκη εισαγωγής
                        </Button> */}
                        {!selectionButton && (() => {
                            const ecrfsForTrial = (context?.ecrfs ?? []).filter(e => e.trial?.id == context?.selection?.clinicalTrial?.id);
                            const pa = patientAdmissions?.find(pa => pa.patientId == record.id);

                            // Screened-out patients are recorded for the statistics but
                            // collect no CRF data, so the way in is closed. Disabled
                            // rather than hidden: a missing button looks like a bug,
                            // while a disabled one with a tooltip explains itself.
                            if (!isPatientEligible(record)) {
                                return (
                                    <Tooltip title="Ο ασθενής δεν πληροί τα κριτήρια της μελέτης.">
                                        <Button type="primary" disabled>
                                            <FormOutlined />Συμπλήρωση
                                        </Button>
                                    </Tooltip>
                                );
                            }

                            const setSelectionAndNavigate = (ecrfId?: number) => {
                                setContext(prevContext => {
                                    return {
                                        ...prevContext,
                                        selection: {
                                            ...prevContext.selection,
                                            patient: record,
                                            admission: pa
                                        } as any
                                    };
                                });
                                if (ecrfId) navigate(`/ecrf/${ecrfId}`);
                            }

                            if (!ecrfsForTrial || ecrfsForTrial.length <= 1) {
                                const ecrf = ecrfsForTrial?.[0];
                                return <Button type="primary" onClick={() => setSelectionAndNavigate(ecrf?.id)}>
                                    <FormOutlined />Συμπλήρωση
                                </Button>
                            }

                            // Every eCRF for the trial is offered in the menu. The main
                            // button must NOT silently pick one - defaulting it to
                            // ecrfsForTrial[0] sent the user to the wrong eCRF and their
                            // edits were then saved against that eCRF's form.
                            const items = ecrfsForTrial.map(e => ({ key: e.id.toString(), label: e.name ?? `#${e.id}` }));

                            return (
                                <Dropdown
                                    menu={{ items, onClick: (info) => { const id = parseInt(info.key as string); setSelectionAndNavigate(id); } }}
                                    trigger={["click"]}
                                >
                                    <Button type="primary">
                                        <FormOutlined />Συμπλήρωση
                                    </Button>
                                </Dropdown>
                            )
                        })()
                        }
                        {/* <Button danger icon={<DeleteOutlined />} onClick={() => {
                            Modal.confirm({
                                title: 'Διαγραφή ασθενή',
                                icon: <ExclamationCircleOutlined />,
                                content: `Είστε σίγουρος οτι θέλετε να διαγράψετε τον ασθενή;`,
                                okText: 'Ναι',
                                cancelText: 'Όχι',
                                onOk: () => { onPatientDelete(record) },
                                onCancel: () => { },
                            });
                        }}>
                            Διαγραφή
                        </Button> */}
                    </Space.Compact >
                ),
            },
        ];

        const onPatientDelete = async (patient: Patient) => {
            await deletePatient(patient.id);
            onDelete?.(patient);
        }

        /** Returns true when the patient was actually saved. */
        const onPatientEdit = async (patient: Patient): Promise<boolean> => {
            const result = await postPatient(patient);

            if (!result.ok) {
                // Show what the server actually said - a duplicate code names the
                // code, a validation failure names the field. The generic sentence
                // is only a fallback for a response with no body at all.
                message.error(result.error ?? "Συνέβει ένα σφάλμα κατα την αποθήκευση του ασθενή");
                // Do NOT refresh on failure: onEdit reloads the list, which made a
                // rejected save look like it had worked.
                return false;
            }

            onEdit?.(patient);
            return true;
        }

        const onPatientAdmissionEdit = async (patientAdmission: PatientAdmission) => {
            await postPatientAdmission(patientAdmission);
            onEdit?.();
        }
        const exportExcel = () => {
            let trialId = context.selection?.clinicalTrial?.id
            window.open(`/api/ClinicalTrial/Export?id=${trialId}`, '_blank');
        }

        return (
            <Card title="Ασθενείς" bordered hoverable className="always" style={{ marginTop: "24px" }}
                extra={<Space.Compact>
                    <Button type="default" onClick={() => { exportExcel(); }}><ExportOutlined /> Εξαγωγή </Button>
                    <Button type="primary" onClick={() => { showForm(undefined); }}><PlusOutlined /> Προσθήκη ασθενή</Button>
                </Space.Compact>
                }>
                <Table columns={columns} dataSource={patients.filter(u => u.patientName.toLowerCase().includes(patientAdmissionsFilter) || u.patientDescription?.toLowerCase().includes(patientAdmissionsFilter))} rowKey="id"
                    // Tints the whole row for a screened-out patient, so they are
                    // identifiable while scanning the list rather than only by
                    // reading the eligibility column.
                    rowClassName={(record: Patient) =>
                        isPatientEligible(record) ? "" : "patient-row-ineligible"}
                    pagination={{ position: ["topRight"], responsive: true, defaultPageSize: 20, total: patients.length, showSizeChanger: true, showQuickJumper: true, showTotal: (total: number) => <Space direction="horizontal" size={5}><Input.Search value={patientAdmissionsFilter} onChange={(e) => { setPatientAdmissionsFilter(e.target.value) }} placeholder="Αναζήτηση" enterButton style={{}} /> <span>{total} Ασθενείς</span></Space> }}
                />
                {/* onFinish returns whether the save succeeded, and the popup closes
                    itself only then. It used to call setFormVisible(false) first and
                    save afterwards, so a rejected save - a duplicate patient code, say
                    - closed the dialog and discarded everything the user had typed,
                    including both criteria tabs. They are only two clicks from the
                    message; they should not have to re-enter the patient. */}
                <PatientPopup
                    visible={formVisible}
                    onCancel={() => setFormVisible(false)}
                    onFinish={onPatientEdit}
                    onSaved={() => setFormVisible(false)}
                    existingPatients={patients}
                    patient={currentPatient} />
                <PatientAdmissionPopup patients={patients} visible={admissionFormVisible} onCancel={() => setAdmissionFormVisible(false)} onFinish={(values: any) => { setAdmissionFormVisible(false); onPatientAdmissionEdit(values); }} patientAdmission={currentPatientAdmission}></PatientAdmissionPopup>
            </Card>
        );
    };

export const PatientPopup: React.FC<{
    visible: boolean,
    onCancel: () => void,
    /** Saves the patient. Resolves true only when the server accepted it. */
    onFinish: (patient: Patient) => Promise<boolean> | boolean,
    /** Called after a successful save, so the popup closes only then. */
    onSaved?: () => void,
    /** Everything already on screen, used for the local duplicate-code check. */
    existingPatients?: Patient[],
    patient?: Patient
}> = ({ visible, onCancel, onFinish, onSaved, existingPatients, patient: patient }) => {
    const [context, setContext] = useContext(AppContext);
    const [form] = Form.useForm();
    const { modal } = App.useApp();
    const userTrials = context.user?.trials;
    const [activeTab, setActiveTab] = useState("1");
    const [saving, setSaving] = useState(false);


    useEffect(() => {
        if (patient) {
            form.setFieldsValue({ ...patient, agreement: true });
        }
        else {
            form.setFieldsValue({ patientName: "", patientDescription: "", clinicalTrialId: userTrials?.[0]?.id });
        }

        if (!visible) {
            setActiveTab("1");
        }
    }, [patient, visible, userTrials]);

    const requiredCheckbox = (message: string) => {
        return {
            validator: (a: any, value: any) => {
                return value ? Promise.resolve() : Promise.reject(message);
            }
        }
    }

    const notRequiredCheckbox = (message: string) => {
        return {
            validator: (a: any, value: any) => {
                return !value ? Promise.resolve() : Promise.reject(message);
            }
        }
    }

    const getInclusionCriteriaList = () => {
        try {
            let parsedJson: InclusionCriteria = JSON.parse(context?.selection?.clinicalTrial?.inclusionCriteriaJson ?? "");
            return parsedJson.inclusion;
        }
        catch {
            return [];
        }
    }

    const getExclusionCriteriaList = () => {
        try {
            let parsedJson: ExclusionCriteria = JSON.parse(context?.selection?.clinicalTrial?.exclusionCriteriaJson ?? "");
            return parsedJson.exclusion;
        }
        catch {
            return [];
        }
    }

    /**
     * Read the criteria answers out of the form and report which ones failed.
     *
     * Inclusion boxes are "patient meets this" - unticked is a failure.
     * Exclusion boxes are "patient has this" - ticked is a failure.
     *
     * Returns the criterion TEXT, not its index: the server snapshots what it is
     * sent, and the trial's criteria list can be edited later (it was in August,
     * when «Εγκυμοσύνη» was removed). An index would quietly come to mean a
     * different criterion and rewrite the history of why a patient was excluded.
     */
    const collectScreeningFailures = () => {
        const values = form.getFieldsValue(true);
        const failedInclusion = getInclusionCriteriaList()
            .filter((_, i) => !values[`inclusion_${i}`]);
        const metExclusion = getExclusionCriteriaList()
            .filter((_, i) => !!values[`exclusion_${i}`]);
        return { failedInclusion, metExclusion };
    };

    /**
     * Save, and only clear and close if the server accepted it.
     *
     * The fields are reset AFTER the await, not before it. Resetting first meant a
     * rejected save - a duplicate code is the common one - left the user staring at
     * an empty form with everything they had typed gone, including both criteria
     * tabs they had just worked through.
     */
    /** Edit path: same guarded save, but sends no screening verdict. */
    const submitEdit = async (values: any) => {
        setSaving(true);
        try {
            const ok = await onFinish({ ...values, id: patient?.id });
            if (ok) {
                form.resetFields();
                setActiveTab("1");
                onSaved?.();
            }
        }
        finally {
            setSaving(false);
        }
    };

    const submit = async (values: any, screening: { failedInclusion: string[]; metExclusion: string[] }) => {
        setSaving(true);
        try {
            const ok = await onFinish({ ...values, id: patient?.id, screening });
            if (ok) {
                form.resetFields();
                setActiveTab("1");
                onSaved?.();
            }
            // On failure the popup stays open with everything intact; the caller has
            // already shown the server's message.
        }
        finally {
            setSaving(false);
        }
    };

    const onOkClick = () => {
        // Only the identity tab still blocks. The criteria tabs used to refuse to
        // save a patient who failed screening, which meant the study had no record
        // of who was screened out or why - exactly the statistic the protocol asks
        // for. They are now recorded and marked ineligible instead.
        const fieldList = activeTab == "1"
            ? ["clinicalTrialId", "patientName", "patientDescription", "agreement"]
            : [];

        form
            .validateFields(fieldList.length > 0 ? fieldList : undefined)
            .then(values => {
                if (patient == null && activeTab != "3") {
                    setActiveTab(activeTab == "1" ? "2" : "3");
                    return;
                }

                // Editing an existing patient does not re-run screening: the edit
                // dialog has no criteria tabs, so sending a verdict from an empty
                // form would mark everyone ineligible. `undefined` screening tells
                // the server to leave the existing verdict alone.
                //
                // Goes through the same guarded save as a new patient, so a rejected
                // edit keeps the dialog open with the values intact.
                if (patient != null) {
                    submitEdit(values);
                    return;
                }

                const screening = collectScreeningFailures();
                const ineligible = screening.failedInclusion.length > 0
                    || screening.metExclusion.length > 0;

                if (!ineligible) {
                    submit(values, screening);
                    return;
                }

                // Confirm before recording someone as ineligible. It is a clinical
                // judgement with consequences - the CRF is closed for them - and a
                // single mis-ticked box should not make it silently.
                modal.confirm({
                    title: "Ο ασθενής δεν πληροί τα κριτήρια",
                    width: 560,
                    icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
                    content: (
                        <div>
                            {screening.failedInclusion.length > 0 && (
                                <>
                                    <div style={{ fontWeight: 600, marginTop: 8 }}>Δεν πληροί τα κριτήρια ένταξης:</div>
                                    <ul style={{ marginTop: 4, paddingInlineStart: 20 }}>
                                        {screening.failedInclusion.map(t => <li key={t}>{t}</li>)}
                                    </ul>
                                </>
                            )}
                            {screening.metExclusion.length > 0 && (
                                <>
                                    <div style={{ fontWeight: 600, marginTop: 8 }}>Πληροί κριτήρια αποκλεισμού:</div>
                                    <ul style={{ marginTop: 4, paddingInlineStart: 20 }}>
                                        {screening.metExclusion.map(t => <li key={t}>{t}</li>)}
                                    </ul>
                                </>
                            )}
                            <div style={{ marginTop: 12 }}>
                                Ο ασθενής θα καταγραφεί για στατιστικούς λόγους, αλλά δεν θα
                                είναι δυνατή η συμπλήρωση του CRF.
                            </div>
                        </div>
                    ),
                    okText: "Αποθήκευση ως μη επιλέξιμος",
                    okButtonProps: { danger: true },
                    cancelText: "Επιστροφή",
                    // Returned, not fired and forgotten: antd keeps the confirm in its
                    // loading state until the promise settles, so the save cannot be
                    // triggered twice and the confirm does not vanish before it is done.
                    onOk: () => submit(values, screening),
                    // The popup behind stays open on failure, so the user lands back on
                    // their filled-in form rather than on nothing.
                });
            })
            .catch(info => {
                console.log(form.getFieldsError());
                console.log(activeTab, 'Τα δεδομένα δεν είναι έγκυρα', info);
            });
    }

    const onPreviousClick = () => {
        setActiveTab(activeTab == "3" ? "2" : "1");
    }

    const onCancelClick = () => {
        form.resetFields();
        onCancel();
    }

    const tabItems = [
        {
            key: "1",
            label: "Στοιχεία ασθενούς",
            children: (
                <>
                    <Form.Item key="0" name="clinicalTrialId" label="Κλινική δοκιμή" rules={[{ required: true, message: 'Επιλέξτε μια κλινική δοκιμή' }]}>
                        <Select options={userTrials?.map(ut => { return { value: ut.id, label: ut.title } })} />
                    </Form.Item>
                    {/* The duplicate check runs here as well as on the server.
                        The server's is the one that counts - it sees every centre's
                        patients and cannot be bypassed - but it only answers after a
                        round trip and three tabs of work. This catches the common case
                        while the user is still typing the code.

                        Compared trimmed and case-insensitively, matching the server, so
                        the two never disagree about what "the same code" means. */}
                    <Form.Item
                        key="1"
                        name="patientName"
                        label="Κωδικός ασθενούς"
                        validateFirst
                        rules={[
                            { required: true, message: 'Εισάγετε το κωδικό του ασθενή' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const code = `${value ?? ""}`.trim().toLowerCase();
                                    if (!code) return Promise.resolve();

                                    const trialId = getFieldValue("clinicalTrialId");
                                    const clash = (existingPatients ?? []).some(p =>
                                        p.id !== patient?.id
                                        && p.clinicalTrialId === trialId
                                        && `${p.patientName ?? ""}`.trim().toLowerCase() === code);

                                    return clash
                                        ? Promise.reject(`Υπάρχει ήδη ασθενής με τον κωδικό "${`${value}`.trim()}" σε αυτό το κέντρο.`)
                                        : Promise.resolve();
                                },
                            }),
                        ]}>
                        <Input />
                    </Form.Item>
                    <Form.Item key="2" name="patientDescription" label="Περιγραφή" rules={[{ required: false, message: 'Εισάγετε μια περιγραφή' }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item
                        key="3"
                        name="agreement"
                        valuePropName="checked"
                        rules={[
                            requiredCheckbox("Παρακαλώ για την συγκατάθεση του ασθενή")
                        ]}
                    >
                        <Checkbox>
                            Ο ασθενής συμφωνεί και έχει υπογράψει την <a href={`Uploads/${context?.selection?.clinicalTrial?.patientConsentFile}`} target="_blank">φόρμα συγκατάθεσης</a>
                        </Checkbox>
                    </Form.Item>
                </>
            )
        },
        ...(!patient ? [
            {
                key: "2",
                label: "Κριτήρια αποκλεισμού",
                children: (
                    <>
                        {/* No validator: ticking an exclusion criterion used to block
                            the save, so an excluded patient left no trace. It now
                            records them as ineligible after a confirmation. */}
                        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                            Επιλέξτε όσα ισχύουν για τον ασθενή.
                        </Typography.Text>
                        {getExclusionCriteriaList().map((ic, i) => (<Form.Item
                            key={i}
                            style={{ marginBottom: "0px" }}
                            name={`exclusion_${i}`}
                            valuePropName="checked"
                            initialValue={false}>
                            <Checkbox><span style={{ whiteSpace: "pre-wrap" }}>{ic}</span></Checkbox>
                        </Form.Item>))}
                    </>
                )
            },
            {
                key: "3",
                label: "Κριτήρια επιλογής",
                children: (
                    <>
                        {/* No validator, for the same reason as the exclusion tab. */}
                        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                            Επιλέξτε όσα πληροί ο ασθενής.
                        </Typography.Text>
                        {getInclusionCriteriaList().map((ic, i) => (<Form.Item
                            key={i}
                            style={{ marginBottom: "0px" }}
                            name={`inclusion_${i}`}
                            valuePropName="checked"
                            initialValue={false}>
                            <Checkbox><span style={{ whiteSpace: "pre-wrap" }}>{ic}</span></Checkbox>
                        </Form.Item>))}
                    </>
                )
            }
        ] : [])
    ];

    return (
        <Modal
            title={patient ? 'Επεξεργασία ασθενή' : 'Νέος ασθενής'}
            destroyOnClose
            width={"70%"}
            open={visible}
            onCancel={onCancelClick}
            // Closing is now driven by a successful save, so the backdrop and the X
            // must not bypass it - a stray click outside would otherwise discard the
            // form exactly the way the old close-first behaviour did.
            maskClosable={!saving}
            closable={!saving}
            footer={<Space.Compact>
                <Button onClick={onCancelClick} disabled={saving}>Ακύρωση</Button>
                {patient == null &&
                    <Button style={{ marginLeft: 0 }} type="default" onClick={onPreviousClick} disabled={saving || activeTab == "1"}>Προηγούμενο</Button>
                }
                <Button style={{ marginLeft: 0 }} type="primary" onClick={onOkClick} loading={saving}>{(activeTab == "3" || patient != null) ? "Αποθήκευση" : "Επομενο"}</Button>
            </Space.Compact >}
        >
            <Form form={form} layout="vertical">
                <Tabs className="patienttabs" activeKey={activeTab} items={tabItems} />
            </Form>
        </Modal >
    );
};